/**
 * Shipclues tracking status codes (31) — aligned with Careveli backend PR #75.
 * Human labels should come from API `latestTrackingStatusLabel`; these are fallbacks only.
 */
export const SHIPCLUES_TRACKING_STATUS_CODES = [
  'PENDING',
  'MANIFESTED',
  'SHIPMENT_BOOKED',
  'OFP',
  'PICKUP_SCHEDULED',
  'PICKUP_FAILED',
  'PICKUP_RESCHEDULED',
  'HOC',
  'PICKED_UP',
  'PACKED',
  'QC_PASSED',
  'QC_FAILED',
  'IN_TRANSIT',
  'AT_DESTINATION_HUB',
  'DELAYED',
  'OFD',
  'NDR',
  'DELIVERED',
  'PARTIALLY_DELIVERED',
  'UNDELIVERED',
  'CANCELLED',
  'LOST',
  'DAMAGED',
  'ON_HOLD',
  'RTO_INITIATED',
  'RTO_IN_TRANSIT',
  'RTO_OFD',
  'RTO_NDR',
  'RTO_DELIVERED',
  'RTO_OUT_FOR_DELIVERY',
  'RETURNED_TO_SELLER',
];

export const SHIPCLUES_CODE_FALLBACK_LABELS = {
  PENDING: 'Pending',
  MANIFESTED: 'Manifested',
  SHIPMENT_BOOKED: 'Shipment booked',
  OFP: 'Out for pickup',
  PICKUP_SCHEDULED: 'Pickup scheduled',
  PICKUP_FAILED: 'Pickup failed',
  PICKUP_RESCHEDULED: 'Pickup rescheduled',
  HOC: 'Handover complete',
  PICKED_UP: 'Picked up',
  PACKED: 'Packed',
  QC_PASSED: 'QC passed',
  QC_FAILED: 'QC failed',
  IN_TRANSIT: 'In transit',
  AT_DESTINATION_HUB: 'At destination hub',
  DELAYED: 'Delayed',
  OFD: 'Out for delivery',
  NDR: 'NDR',
  DELIVERED: 'Delivered',
  PARTIALLY_DELIVERED: 'Partially delivered',
  UNDELIVERED: 'Undelivered',
  CANCELLED: 'Cancelled',
  LOST: 'Lost',
  DAMAGED: 'Damaged',
  ON_HOLD: 'On hold',
  RTO_INITIATED: 'RTO initiated',
  RTO_IN_TRANSIT: 'RTO in transit',
  RTO_OFD: 'RTO out for delivery',
  RTO_NDR: 'RTO NDR',
  RTO_DELIVERED: 'RTO delivered',
  RTO_OUT_FOR_DELIVERY: 'RTO out for delivery',
  RETURNED_TO_SELLER: 'Returned to seller',
};

export const normalizeShipcluesTrackingCode = (code) =>
  String(code || '')
    .trim()
    .toUpperCase()
    .replace(/\s+/g, '_');

export const humanizeShipcluesCode = (code) => {
  const normalized = normalizeShipcluesTrackingCode(code);
  if (SHIPCLUES_CODE_FALLBACK_LABELS[normalized]) {
    return SHIPCLUES_CODE_FALLBACK_LABELS[normalized];
  }
  return normalized.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
};

/** Badge colour group for UI */
export const getShipcluesCodeGroup = (code) => {
  const c = normalizeShipcluesTrackingCode(code);
  if (!c) return 'unknown';
  if (c === 'CANCELLED') return 'cancelled';
  if (c.startsWith('RTO_') || c === 'RETURNED_TO_SELLER') return 'rto';
  if (c === 'DELIVERED' || c === 'PARTIALLY_DELIVERED') return 'delivered';
  if (c === 'OFD') return 'ofd';
  if (
    [
      'PENDING',
      'MANIFESTED',
      'SHIPMENT_BOOKED',
      'OFP',
      'PICKUP_SCHEDULED',
      'PICKUP_FAILED',
      'PICKUP_RESCHEDULED',
      'HOC',
      'PICKED_UP',
      'PACKED',
      'QC_PASSED',
    ].includes(c)
  ) {
    return 'pickup';
  }
  if (
    [
      'IN_TRANSIT',
      'AT_DESTINATION_HUB',
      'DELAYED',
      'DAMAGED',
      'ON_HOLD',
      'LOST',
      'UNDELIVERED',
      'NDR',
      'QC_FAILED',
    ].includes(c)
  ) {
    return 'transit';
  }
  return 'transit';
};

export const getShipcluesBadgePresentation = (code) => {
  const group = getShipcluesCodeGroup(code);
  switch (group) {
    case 'pickup':
      return { bsVariant: null, className: 'order-shipment-badge--pickup' };
    case 'transit':
      return { bsVariant: 'info', className: 'order-shipment-badge--transit' };
    case 'ofd':
      return { bsVariant: 'warning', className: 'order-shipment-badge--ofd' };
    case 'delivered':
      return { bsVariant: 'success', className: '' };
    case 'rto':
      return { bsVariant: null, className: 'order-shipment-badge--rto' };
    case 'cancelled':
      return { bsVariant: 'danger', className: '' };
    default:
      return { bsVariant: 'secondary', className: '' };
  }
};
